import { useState, useEffect, useCallback, useRef } from 'react';
import { DialogueLine } from '../types';

const DIARIZE_TIMEOUT_MS = 15_000;
const DIARIZE_MAX_ATTEMPTS = 3;

async function postDiarizeWithTimeout(clipId: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch('/api/diarize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId }),
      signal: controller.signal
    });
  } finally {
    clearTimeout(timer);
  }
}

interface UseStudioDiarizationOptions {
  clipId: string;
  duration: number;
  hasStems: boolean;
  hasDialogueLines?: boolean;
  autoDiarize?: boolean;
  onDialogueLoaded: (enrichedLines: DialogueLine[]) => void;
  addTelemetryLog?: (message: string, type: 'info' | 'pending' | 'success' | 'warn' | 'dispatch') => void;
}

export function useStudioDiarization({
  clipId,
  duration,
  hasStems,
  hasDialogueLines,
  autoDiarize = true,
  onDialogueLoaded,
  addTelemetryLog
}: UseStudioDiarizationOptions) {
  const [isLoadingDialogue, setIsLoadingDialogue] = useState<boolean>(false);
  const [diarizeError, setDiarizeError] = useState<string | null>(null);
  const [diarizeCompleted, setDiarizeCompleted] = useState<boolean>(false);

  // Strict concurrency mutex & execution guard (RULE 1)
  const isDiarizingRef = useRef<boolean>(false);
  const autoDiarizedClipRef = useRef<string | null>(null);
  const prevClipIdRef = useRef<string | null>(null);

  // Keep latest callbacks in refs to prevent unnecessary re-instantiations and render loops
  const onDialogueLoadedRef = useRef(onDialogueLoaded);
  onDialogueLoadedRef.current = onDialogueLoaded;
  const addTelemetryLogRef = useRef(addTelemetryLog);
  addTelemetryLogRef.current = addTelemetryLog;
  const durationRef = useRef(duration);
  durationRef.current = duration;

  // Reset error & tracking when clip changes
  useEffect(() => {
    if (clipId !== prevClipIdRef.current) {
      prevClipIdRef.current = clipId;
      autoDiarizedClipRef.current = null;
      isDiarizingRef.current = false;
      setDiarizeError(null);
      setDiarizeCompleted(false);
      setIsLoadingDialogue(false);
    }
  }, [clipId]);

  const handleSkipDiarization = useCallback(() => {
    if (!clipId) return;
    autoDiarizedClipRef.current = clipId;
    isDiarizingRef.current = false;
    setDiarizeCompleted(true);
    setIsLoadingDialogue(false);
    setDiarizeError(null);
    if (addTelemetryLogRef.current) {
      addTelemetryLogRef.current('Speech diarization skipped. You can manually enter dialogue lines anytime.', 'info');
    }
  }, [clipId]);

  const handleRunDiarization = useCallback(async (force: boolean = false) => {
    if (!clipId) return;

    // Concurrency guard: Only ONE diarization request can be in-flight at any time
    if (isDiarizingRef.current) return;

    // RULE: Groq Whisper Diarization must only run once Demucs stems are baked in R2
    if (!hasStems && !force) {
      setDiarizeError('Waiting for Demucs audio stem separation to finish before diarizing...');
      if (addTelemetryLogRef.current) {
        addTelemetryLogRef.current('Diarization paused: Waiting for Demucs vocal stem isolation...', 'pending');
      }
      return;
    }

    // Atomically claim the run for this clipId to block concurrent triggers
    isDiarizingRef.current = true;
    autoDiarizedClipRef.current = clipId;
    setIsLoadingDialogue(true);
    setDiarizeError(null);

    try {
      for (let attempt = 1; attempt <= DIARIZE_MAX_ATTEMPTS; attempt++) {
        let res: Response;
        try {
          res = await postDiarizeWithTimeout(clipId, DIARIZE_TIMEOUT_MS);
        } catch (err) {
          // Network error or timeout (AbortError) — retry
          if (attempt === DIARIZE_MAX_ATTEMPTS) {
            setDiarizeError('Diarization timed out. Please retry.');
          } else if (addTelemetryLogRef.current) {
            addTelemetryLogRef.current(`Diarization attempt ${attempt} timed out — retrying...`, 'warn');
          }
          continue;
        }

        const data = (await res.json().catch(() => null)) as any;

        if (res.ok && data?.dialogue && data.dialogue.length > 0) {
          const currentDur = durationRef.current;
          const enrichedDialogue: DialogueLine[] = data.dialogue.map((d: any, idx: number) => ({
            ...d,
            id: d.id || `line-${idx + 1}`,
            rawStart: d.rawStart !== undefined ? d.rawStart : (d.start ?? d.startTime ?? 0),
            rawEnd: d.rawEnd !== undefined ? d.rawEnd : (d.end ?? d.endTime ?? currentDur),
            words: Array.isArray(d.words) ? d.words : []
          }));

          setDiarizeCompleted(true);
          onDialogueLoadedRef.current(enrichedDialogue);

          if (addTelemetryLogRef.current) {
            addTelemetryLogRef.current(`Groq Whisper parsed ${enrichedDialogue.length} dialogue lines from clean Demucs vocals`, 'success');
          }
          return;
        }

        if (res.ok && Array.isArray(data?.dialogue) && data.dialogue.length === 0) {
          setDiarizeCompleted(true);
          onDialogueLoadedRef.current([]);
          if (addTelemetryLogRef.current) {
            addTelemetryLogRef.current('Groq Whisper speech diarization: No dialogue detected in scene (instrumental/silent).', 'info');
          }
          return;
        }

        // Terminal error: unauthorized (missing Groq API key)
        if (res.status === 401) {
          setDiarizeError(data?.message || data?.details || data?.error || 'Groq API Key is required for speech diarization.');
          return;
        }

        // 404: Audio stem still propagating in R2 storage — retry if attempts remain
        if (res.status === 404 && attempt < DIARIZE_MAX_ATTEMPTS) {
          if (addTelemetryLogRef.current) {
            addTelemetryLogRef.current(`Vocal stems syncing in R2 storage — retrying diarization in 2.5s (attempt ${attempt}/${DIARIZE_MAX_ATTEMPTS})...`, 'pending');
          }
          await new Promise((r) => setTimeout(r, 2500));
          continue;
        }

        // Transient failure or exhausted attempts
        if (attempt === DIARIZE_MAX_ATTEMPTS) {
          setDiarizeError(data?.message || data?.error || 'Diarization timed out. Please retry.');
        } else if (addTelemetryLogRef.current) {
          addTelemetryLogRef.current(`Diarization attempt ${attempt} failed — retrying...`, 'warn');
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    } finally {
      isDiarizingRef.current = false;
      setIsLoadingDialogue(false);
    }
  }, [clipId, hasStems]);

  // Auto-diarize ONLY once Demucs stems are verified ready for this clip
  useEffect(() => {
    if (!clipId || !hasStems) return;
    if (!autoDiarize) return;

    // Do not auto-re-run if already diarized or in flight for this clip
    if (autoDiarizedClipRef.current === clipId || isDiarizingRef.current) return;

    // If active entry already has populated dialogue lines, mark as handled and avoid overwriting user edits
    if (hasDialogueLines) {
      autoDiarizedClipRef.current = clipId;
      setDiarizeCompleted(true);
      return;
    }

    if (addTelemetryLogRef.current) {
      addTelemetryLogRef.current('Demucs stems verified ready in R2. Triggering Groq Whisper diarization...', 'dispatch');
    }
    handleRunDiarization(true);
  }, [clipId, hasStems, hasDialogueLines, handleRunDiarization]);

  return {
    isLoadingDialogue,
    diarizeError,
    diarizeCompleted,
    handleRunDiarization,
    handleSkipDiarization
  };
}
